/**
 * Fire-time rules every order source shares — spec B4 / B6
 * (docs/superpowers/specs/2026-09-24-order-source-unification-design.md).
 *
 * The owner's rule: an order's source may change only how it ENTERS and how
 * it is PAID for. Whether the kitchen may start it, and whether we owe it a
 * courier when it does, are properties of the order — read here, once, by the
 * kitchen orchestration (every send, from every caller) and by the acceptance
 * executor (every fire).
 */

import { isMarketplaceOrder } from './marketplaceOrder';
import { isCheckoutHeldOrderState } from './stateMachines';

/** Why the kitchen may not be sent an order yet. */
export type KitchenHoldCode =
    /** `scheduled`: held until `fireAt` (or not yet given one). */
    | 'ORDER_HELD_UNTIL_FIRE'
    /** `pendingAcceptance`: nobody has accepted it. */
    | 'ORDER_AWAITING_ACCEPTANCE'
    /**
     * `awaitingPayment` / `abandoned`: an online order persisted before its
     * charge — nobody has paid for it, and it may never be paid for.
     */
    | 'ORDER_AWAITING_PAYMENT';

/**
 * Why a kitchen status write was refused, machine-readable. The hold codes,
 * plus D4's `ITEM_NOT_SENT_TO_KITCHEN`: every requested line was one the
 * kitchen never received, so it cannot be readied or served — send it first.
 */
export type KitchenRefusalCode = KitchenHoldCode | 'ITEM_NOT_SENT_TO_KITCHEN';

export interface KitchenHold {
    readonly code: KitchenHoldCode;
    /** When the held order fires, when it has a fire time. */
    readonly heldUntil?: Date;
}

const toDate = (value: unknown): Date | undefined => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate();
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? undefined : d;
    }
    return undefined;
};

/**
 * Whether sending [order] to the kitchen must be REFUSED right now, and why.
 *
 * 🔴 ONLY THE TILL'S MAIN SEND HONOURED A HOLD. The server's kitchen send had
 * no state gate, so the till's other send paths (the kitchen dialog, the exit
 * prompt's "send unsent lines", send-all-pending) and any server caller could
 * put a 5:00 PM order on the line at noon. The gate now lives in the one place
 * every send passes — the kitchen orchestration — and this is its rule:
 *
 *   - `scheduled` before `fireAt` (or with no `fireAt` at all — accepted by
 *     nobody yet): held. The activator, or Start Now, OPENS it first; an open
 *     order is never held.
 *   - `pendingAcceptance`: nobody has accepted it; the kitchen must not start
 *     food the restaurant may still turn down.
 *   - `awaitingPayment` / `abandoned` (checkout-held): nobody has paid for it.
 *
 * A `scheduled` order whose `fireAt` has arrived is let through: it is due,
 * and refusing it would only strand it if the activator is late.
 */
export function kitchenSendHold(
    order: { readonly orderState?: unknown; readonly fireAt?: unknown },
    now: Date = new Date(),
): KitchenHold | null {
    // A checkout-held order is not an order the kitchen may start: its card has
    // not been charged (docs/superpowers/specs/2026-09-30-online-order-persist-first-design.md).
    if (isCheckoutHeldOrderState(order.orderState)) {
        return { code: 'ORDER_AWAITING_PAYMENT' };
    }
    if (order.orderState === 'pendingAcceptance') {
        return { code: 'ORDER_AWAITING_ACCEPTANCE' };
    }
    if (order.orderState !== 'scheduled') return null;
    const fireAt = toDate(order.fireAt);
    if (!fireAt) return { code: 'ORDER_HELD_UNTIL_FIRE' };
    return fireAt.getTime() > now.getTime()
        ? { code: 'ORDER_HELD_UNTIL_FIRE', heldUntil: fireAt }
        : null;
}

/**
 * Does this order need a courier that WE book when it fires?
 *
 * A delivery the restaurant fulfils — whether it came in online or was rung in
 * on a till. Never a marketplace order: the marketplace carries it with its own
 * courier, and booking ours would send a second driver for the same bag.
 *
 * Read by the courier dispatch itself, so every caller (the shared fire path,
 * a staff accept, a provisioning test) gets the same answer.
 */
export function needsLocationCourier(order: {
    readonly type?: string | null;
    readonly orderType?: string | null;
    readonly orderSource?: string | null;
    readonly externalSource?: string | null;
}): boolean {
    const type = order.type ?? order.orderType;
    return type === 'delivery' && !isMarketplaceOrder(order);
}

/**
 * The flag a line carries when it could not be sold at the moment its order
 * FIRED — on the kitchen ticket and on the till.
 *
 * ACCEPTED = OWED (owner decision 2026-09-25). Once an order is accepted — by
 * staff, by the restaurant's auto policy, by a marketplace — an item that goes
 * unavailable before it fires never denies, cancels or refunds the order. The
 * order fires; the people who can act (86 / substitute / call the guest) are
 * told, on the line itself.
 *
 * Plain ASCII on purpose: it is printed by thermal printers whose code pages
 * do not carry emoji or box-drawing glyphs.
 */
export const UNAVAILABLE_AT_FIRE_NOTE_PREFIX = 'UNAVAILABLE AT FIRE';

/** One line found unavailable when its (already accepted) order fired. */
export interface UnavailableLineFlag {
    readonly orderItemGuid: string;
    /** What validation said, e.g. "Item is 86'd". */
    readonly reason: string;
}

/**
 * [items] with each flagged line's `kitchenNotes` led by the unavailable flag.
 *
 * `kitchenNotes` because it is the ONE field both kitchen-ticket builders
 * print (`kitchenNotes ?? notes` — `kitchenTicketEngine` and the till's
 * `kitchen_local_ticket_builder`), and the till's order model already carries
 * it, so a till write-back keeps the flag. The guest's own note stays beneath
 * the flag, since `kitchenNotes` replaces `notes` on the ticket. Idempotent: a
 * replayed fire does not stack a second flag.
 */
export function withUnavailableLineNotes<
    T extends {
        readonly orderItemGuid?: string;
        readonly kitchenNotes?: string | null;
        readonly notes?: string | null;
    },
>(items: readonly T[], flags: readonly UnavailableLineFlag[]): T[] {
    if (flags.length === 0) return [...items];
    const byGuid = new Map(flags.map((f) => [f.orderItemGuid, f]));
    return items.map((item) => {
        const flag = item.orderItemGuid ? byGuid.get(item.orderItemGuid) : undefined;
        if (!flag) return item;
        const existing = (item.kitchenNotes ?? item.notes ?? '').trim();
        if (existing.startsWith(UNAVAILABLE_AT_FIRE_NOTE_PREFIX)) return item;
        const line = `${UNAVAILABLE_AT_FIRE_NOTE_PREFIX}: ${flag.reason}`;
        return { ...item, kitchenNotes: existing ? `${line}\n${existing}` : line };
    });
}

/**
 * `Order.stockCommit` — who takes an order's item stock off the shelf.
 * `'atSend'`: the server, at kitchen send/fire, once per line.
 */
export const STOCK_COMMIT_AT_SEND = 'atSend' as const;
export type StockCommitMode = typeof STOCK_COMMIT_AT_SEND;

/**
 * Does the SERVER take this order's item stock off the shelf when food is
 * committed to the kitchen?
 *
 * Owner decision 2026-09-25: stock leaves at send/fire, through one server
 * path, for every source. The till is the one plane that decided it on the
 * device — every build before that decision decremented a line when it was
 * ADDED. Those tills and a new API run side by side during a rollout, in both
 * orders, so the rule must not count a line twice nor skip it:
 *
 *   - not a till order (web, marketplace, kiosk …) → always the server's;
 *   - a till order stamped `stockCommit: 'atSend'` (a till that only RESERVES
 *     locally) → the server's;
 *   - a till order with no stamp → the till that rang it in already took the
 *     stock, so the server does NOT.
 *
 * The till half of the rollout lives in the Dart `KitchenApiService`: a send
 * whose response lacks the server's `stockCommitted: true` (an API before this
 * rule) makes the till take its reserved lines itself, once.
 *
 * ROLLOUT SHIM: once every till in the fleet stamps `atSend`, the till branch
 * collapses to "always the server's".
 */
export function serverTakesStockAtSend(order: {
    readonly orderSource?: string | null;
    readonly externalSource?: string | null;
    readonly stockCommit?: string | null;
}): boolean {
    // A till order: rung in on a till, and not a marketplace order the till
    // merely displays (`orderSource: 'pos'` with a platform `externalSource`).
    const external = (order.externalSource ?? '').toLowerCase();
    const tillOrder = order.orderSource === 'pos' && (external === '' || external === 'internal');
    return !tillOrder || order.stockCommit === STOCK_COMMIT_AT_SEND;
}
